import { cn } from '@ophelos/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { type ComponentProps, useEffect, useRef, useState } from 'react';
import { MOBILE_QUERY, useMediaQuery } from '@/hooks/useMediaQuery';

const bottomActionBarVariants = cva(
  'bottom-0 w-full pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-soft/10 backdrop-blur-lg border-t border-border/50',
  {
    variants: {
      variant: {
        actions: 'fixed px-4 flex flex-col gap-3 items-center justify-center',
        composer: 'sticky',
      },
    },
    defaultVariants: {
      variant: 'actions',
    },
  },
);

export interface BottomActionBarProps extends ComponentProps<'div'>, VariantProps<typeof bottomActionBarVariants> {}

// `data-bottom-bar` is what the mobile chat icon (`AIStickyTrigger`) measures to rest above the bar
// instead of on its main button.
export function BottomActionBar({ variant, className, ...props }: BottomActionBarProps) {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const reserveRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);

  useEffect(() => {
    const node = reserveRef.current?.firstElementChild as HTMLElement | null | undefined;
    if (!node) return;
    const measure = () => setBarHeight(node.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    // Border box, because the home-indicator padding changes alone (Safari's toolbar showing or
    // hiding) leave the content box the same size.
    observer.observe(node, { box: 'border-box' });
    return () => observer.disconnect();
  }, []);

  const bar = <div {...props} data-bottom-bar className={cn(bottomActionBarVariants({ variant, className }))} />;
  if (variant === 'composer') return bar;

  // A fixed bar sits outside the page flow, so this holds its height in the flow plus a 1rem gap. On
  // phones it also holds the chat icon floating above the bar (3.75rem, as ChatStep's spacer
  // reserves), so page content ends 1rem clear of both. It is a centring row too: the bar sets no
  // horizontal inset, so its static position centres it on the page column, which on desktop sits
  // beside the chat panel rather than spanning the screen.
  return (
    <div
      ref={reserveRef}
      className="flex w-full justify-center"
      style={{ height: `calc(${barHeight}px + ${isMobile ? '4.75rem' : '1rem'})` }}
    >
      {bar}
    </div>
  );
}
